import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback
}) => {
  const { isAuthenticated, isLoading, login, error, oktaAuth, userNotAssigned } = useAuth();
  const [hasTriedAuth, setHasTriedAuth] = useState(false);
  const [isAttemptingAuth, setIsAttemptingAuth] = useState(false);

  // Handle normal authentication attempts
  useEffect(() => {
    const attemptAuthentication = async () => {
      // Don't attempt if user is not assigned to the app
      if (userNotAssigned) {
        console.log('User not assigned, skipping auto-authentication');
        return;
      }

      // Don't attempt if already tried, currently attempting, loading, or authenticated
      if (hasTriedAuth || isAttemptingAuth || isLoading || isAuthenticated) {
        return;
      }

      // Don't attempt if oktaAuth is not ready
      if (!oktaAuth) {
        console.log('Waiting for Okta Auth to initialize...');
        return;
      }

      try {
        setIsAttemptingAuth(true);
        setHasTriedAuth(true);
        
        console.log('Attempting authentication...');
        
        // Store the current path for redirect after login
        sessionStorage.setItem('okta-original-uri', window.location.pathname + window.location.search);
        
        await login();
      } catch (err) {
        console.error('Authentication attempt failed:', err);
        setIsAttemptingAuth(false);
      }
    };

    attemptAuthentication();
  }, [isLoading, isAuthenticated, hasTriedAuth, isAttemptingAuth, login, oktaAuth, userNotAssigned]);

  // Show access denied message for users not assigned to the application
  if (userNotAssigned && !isAuthenticated) {
    return <Navigate to="/unassigned" replace />;
  }

  // Show loading state while initializing or attempting auth
  if (isLoading || !oktaAuth || (isAttemptingAuth && !isAuthenticated)) {
    return fallback || (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
      </Box>
    );
  }

  // Show error state if authentication failed
  if (error && hasTriedAuth && !isAuthenticated && !userNotAssigned) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        padding={3}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {error}
        </Typography>
      </Box>
    );
  }

  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Fallback loading state
  return fallback || (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress size={60} sx={{ mb: 3 }} />
    </Box>
  );
};

export default ProtectedRoute;